import { useState } from 'react';
import { View, Text, TextInput, ScrollView, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { Button } from '../common/Button';
import { usePanelStack } from '../layout/PanelStack';
import { usePeopleListsStore } from '../../store/peopleListsStore';
import { EMAIL_SHAPE } from '../../utils/peopleLists';

// "Add a person" in the People panel (Oct 2026): one person typed in by hand,
// saved on Team in the owner's "My people" list for this part of the map.
// This is the only way to add someone to the list Assign picks from without
// uploading a file. Private to the owner, like every list: nobody is emailed.

function Field({
  label,
  value,
  onChangeText,
  testID,
  maxLength,
  placeholder,
  keyboardType,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  testID: string;
  maxLength: number;
  placeholder?: string;
  keyboardType?: 'email-address' | 'phone-pad';
}) {
  const { colors } = useTheme();
  return (
    <View style={styles.field}>
      <Text style={[styles.label, { color: colors.textSecondary }]}>{label}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        maxLength={maxLength}
        placeholder={placeholder}
        placeholderTextColor={colors.textTertiary}
        keyboardType={keyboardType}
        autoCapitalize={keyboardType === 'email-address' ? 'none' : undefined}
        testID={testID}
        accessibilityLabel={label}
        style={[styles.input, { color: colors.text, borderColor: colors.border }]}
      />
    </View>
  );
}

export function AddPersonForm({ areaId, onSaved }: { areaId: string; onSaved?: () => void }) {
  const { colors } = useTheme();
  const { pop } = usePanelStack();
  const addPerson = usePeopleListsStore((s) => s.addPerson);
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = name.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (email.trim() && !EMAIL_SHAPE.test(email.trim())) {
      setError("That email address doesn't look right.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await addPerson(areaId, { name, organization, title, category, email, phone });
      onSaved?.();
      pop();
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? `Couldn't save that person. ${e.message}`
          : "Couldn't save that person. Try again."
      );
      setSaving(false);
    }
  };

  return (
    <View style={styles.root} testID="people-person-form">
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled">
        <Text style={[styles.intro, { color: colors.textSecondary }]}>
          Only you can see this. They go on your Team, and nobody gets emailed.
        </Text>
        <Field
          label="Name"
          value={name}
          onChangeText={setName}
          maxLength={120}
          placeholder="e.g. Maria Lopez"
          testID="people-person-form-name"
        />
        <Field
          label="Organization"
          value={organization}
          onChangeText={setOrganization}
          maxLength={120}
          placeholder="e.g. Pine Ridge HOA"
          testID="people-person-form-organization"
        />
        <Field
          label="Title"
          value={title}
          onChangeText={setTitle}
          maxLength={120}
          placeholder="e.g. Chair"
          testID="people-person-form-title"
        />
        <Field
          label="Type"
          value={category}
          onChangeText={setCategory}
          maxLength={60}
          placeholder="e.g. HOA"
          testID="people-person-form-type"
        />
        <Field
          label="Email"
          value={email}
          onChangeText={setEmail}
          maxLength={254}
          keyboardType="email-address"
          testID="people-person-form-email"
        />
        <Field
          label="Phone"
          value={phone}
          onChangeText={setPhone}
          maxLength={40}
          keyboardType="phone-pad"
          placeholder="e.g. (909) 555-0142"
          testID="people-person-form-phone"
        />
        {error && (
          <Text style={[styles.error, { color: colors.danger }]} testID="people-person-form-error">
            {error}
          </Text>
        )}
      </ScrollView>
      <View style={[styles.footer, { borderColor: colors.border }]}>
        <Button
          title="Cancel"
          onPress={pop}
          variant="secondary"
          size="sm"
          disabled={saving}
          style={styles.footerButton}
          testID="people-person-form-cancel"
        />
        <Button
          title="Save"
          onPress={() => void handleSave()}
          variant="primary"
          size="sm"
          disabled={!canSave}
          loading={saving}
          style={styles.footerButton}
          testID="people-person-form-save"
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, minHeight: 0 },
  scroll: { flex: 1 },
  content: { paddingHorizontal: SPACING.md, paddingBottom: SPACING.lg },
  intro: { ...TYPOGRAPHY.bodySmall, marginBottom: SPACING.md },
  field: { marginBottom: SPACING.sm },
  label: { ...withWeight(TYPOGRAPHY.caption, 'medium'), marginBottom: SPACING.xxs },
  input: {
    ...TYPOGRAPHY.body,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
  },
  error: { ...TYPOGRAPHY.bodySmall },
  footer: {
    flexDirection: 'row',
    gap: SPACING.sm,
    padding: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  footerButton: { flex: 1 },
});
